import React, { useState, KeyboardEvent, useRef, useEffect, ChangeEvent } from 'react';
import { Send } from 'lucide-react';
import { Message } from '../types';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  messages: Message[];
}

export function ChatInput({
  onSendMessage,
  disabled = false,
  placeholder = "Type your message...",
  messages,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    autoResize();
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  return (
    <div 
      className="flex flex-col items-center gap-2 p-2 px-4"
      data-testid="chat-input-container"
    >
      <textarea
        ref={textareaRef}
        value={message}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="w-full resize-none border-none py-2 text-sm focus:outline-none focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed overflow-hidden"
        style={{ minHeight: '44px', maxHeight: '200px' }}
        data-testid="chat-input-textarea"
      />
      <div className="w-full flex justify-end items-center gap-2">
        <button
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-full p-2 transition-colors"
          data-testid="chat-input-send-button"
          aria-label="Send message"
        >
          <Send className="w-3 h-3" />
        </button>
      </div> 
    </div>
  );
}
